import React, {
    FC,
    MutableRefObject,
    ReactElement,
    RefObject,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { ButtonBase, Grow } from '@material-ui/core'
import IconButton, { IconButtonType } from '../../../../../React/Common/Components/IconButton'
import Icon, { IconName } from '../../../../../React/Common/Components/Icon'
import { IconSize } from '../../../../../NonReact/constants/React/Icon/Constants'
import {
    IsStringEmpty,
    isStringNotEmpty,
    IsVarEmpty,
    isVarNotEmpty,
    Or
} from '../../../../../NonReact/utils/GlobalFunctions'
import { UserBadge } from '../../../../../React/Common/Components/UserBadge/UserBadge'
import { LanguageContext, ReactState } from '../../../../../React/Common/Contexts'
import { NotificationIcon } from '../../../../../React/MainMenu/NotificationIcon'
import { AppMenu } from './AppMenu'
import { useDarkMode } from '../../../../../React/Common/Hooks/useDarkMode'
import { Branding } from '../../../../../React/Common/API/BrandingAPI'
import { useBranding } from '../../../../../React/Common/Hooks/useBranding'
import { ReportingAccessLevels } from '../../../../../NonReact/constants/Permissions'
import { useIsInsightsAvailable } from '../../../../Hooks/useIsInsightsAvailable'
import { useIsPresenceAvailable } from '../../../../Hooks/useIsPresenceAvailable'
import { useCheckUserHasPermissions } from '../../../../Hooks/useCheckPermissions'
import { ADMIN_MENU_PERMISSIONS, STATUS_AND_TOOLS_PERMISSIONS } from '../../../../../React/Common/Utils/PermissionsUtil'
import { AppMenu as AppMenuLanguage } from '../../../../../../i18n'
import { MainMenuApp, MainMenuAppTransfer } from '../../../../../NonReact/constants/AppMenuConstants'
import { RxJSCurrentState, useRxJSState } from '../../../../../React/Common/Hooks/useRxJSState'
import { appMenuUpdate$ } from '../../../../../NonReact/common/RxJS/AppMenuState'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { useCurrentPage } from '../../../../Hooks/useCurrentPage'
import { configureFullURLForLocalDevelopment } from '../../../../../React/Common/Utils/LocalUtils'
import { EMPTY_STRING } from '../../../../../NonReact/constants/Common'
import { userPagePermissions$ } from '../../../../../NonReact/common/RxJS/UserPagePermissionsState'
import { UserInfo, useUserInfo } from '../../../../Hooks/useUserInfo'
import { UserMenu } from './UserMenu'
import { isNativeTeamsApp } from '../../../../../React/Common/Utils/TeamsNativeUtils'
import { TransitionPopper } from '../../TransitionPopper/TransitionPopper'
import { AppMenuEntry, NewUISsoResponse, RouteID } from '../Types/Types'
import { buildNewUiSsoUrl, FUSION_TOKEN_QUERY_PARAM } from '../Constants'
import { useUserHasSelfProvisioningPermission } from '../../../../../React/Common/Hooks/permissions/useUserHasSelfProvisioningPermission'
import { useUserHasManageQueuePermission } from '../../../../../React/Common/Hooks/permissions/useUserHasManageQueuesPermission'
import { useUserHasUserManagementPermission } from '../../../../../React/Common/Hooks/permissions/useUserHasUserManagementPermission'
import { Page } from '../../../../../NonReact/constants/Components/Page'
import { useFeatureFlag } from '../../../../../React/Common/Hooks/useFeatureFlag'
import {
    ENABLE_NEW_AGENT_LIST_PAGE,
    ENABLE_NEW_CME_LIST_PAGE,
    ENABLE_NEW_DEVICE_LIST_PAGE,
    ENABLE_NEW_LICENSE_MANAGEMENT_PAGE,
    ENABLE_NEW_SERVER_STATUS_PAGE,
    ENABLE_NEW_PARTITION_LIST_PAGE,
    ENABLE_NEW_CODE_LIST_PAGE,
    ENABLE_NEW_RECORDING_LIST_PAGE,
    ENABLE_NEW_DIRECTORY_LIST_PAGE,
    ENABLE_NEW_TELSYS_LIST_PAGE,
    ENABLE_NEW_USERS_LIST_PAGE,
    ENABLE_NEW_AUDIT_TRAIL_PAGE,
    ENABLE_NEW_LOG_MANAGEMENT_PAGE,
    ENABLE_NEW_LOGGING_SETTINGS_PAGE,
    ENABLE_NEW_TEL_MODULE_STATUS_PAGE,
    ENABLE_NEW_DATABASE_PAGE
} from '../../../../Constants/FeatureFlags'
import { displayError } from '../../../../../React/Common/Utils/Messages'
import { getNewUIConfig, NewUIConfigDTO } from '../../../../../React/Common/API/GenericConfigAPI'
import { JSONResponse } from '../../../../../NonReact/common/Types/Types'
import { UiVersionToggle } from './UiVersionToggle'
import { requestGetJSON } from '../../../../../NonReact/common/API/REST/RESTAPIClient'

type Props = {
    routeID?: RouteID
}

/**
 * Normalises the Generic Config value used for the New UI link.
 *
 * @param config Parsed Generic Config response for the New UI target.
 * @returns A trimmed URL string, or null when the configured value is empty.
 */
const getConfiguredNewUIURL = (config: JSONResponse<NewUIConfigDTO>): Nullable<string> => {
    const configuredURL: string = config?.url?.trim() ?? EMPTY_STRING

    return isStringNotEmpty(configuredURL) ? configuredURL : null
}

export const HeaderTop: FC<Props> = ({ routeID }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(AppMenuLanguage.NAME)

    useDarkMode()

    const [userInfo]: UserInfo = useUserInfo()

    const currentJSPPage: Nullable<Page> = useCurrentPage()

    const branding: Nullable<Branding> = useBranding()

    const anchorElAppLauncher: RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null)
    const anchorElUserMenu: RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null)

    const [isUserMenuOpen, setIsUserMenuOpen]: ReactState<boolean> = useState<boolean>(false)
    const [isAppMenuOpen, setIsAppMenuOpen]: ReactState<boolean> = useState<boolean>(false)
    const [notificationsOpen, setNotificationsOpen]: ReactState<boolean> = useState<boolean>(false)
    const [newUIURL, setNewUIURL]: ReactState<Nullable<string>> = useState<Nullable<string>>(null)
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const fusionToken: Nullable<string> = useMemo(
        (): Nullable<string> => new URLSearchParams(window.location.search).get(FUSION_TOKEN_QUERY_PARAM),
        [window.location.search]
    )

    // Should we show the Reporting entry?
    // ---
    // User has reporting access?
    const hasReportingAccess: Definable<boolean> = useMemo((): Definable<boolean> => {
        if (IsVarEmpty(userInfo)) {
            return undefined
        }

        return userInfo?.Scope?.ReportingAccess !== ReportingAccessLevels.NONE
    }, [userInfo])

    // Is Insights available for the user?
    const isInsightsAvailable: Definable<boolean> = useIsInsightsAvailable()

    // Is Presence available for the user?
    const isPresenceAvailable: Definable<boolean> = useIsPresenceAvailable()

    // Reporting Available?
    const isReportingAvailable: Definable<boolean> = useMemo(
        (): Definable<boolean> => Or(hasReportingAccess, isInsightsAvailable, isPresenceAvailable),
        [hasReportingAccess, isInsightsAvailable, isPresenceAvailable]
    )

    const isBroadworksProvisioningWizardAvailable: Definable<boolean> = useUserHasSelfProvisioningPermission()
    const isQueueManagementAvailable: Definable<boolean> = useUserHasManageQueuePermission()
    const isUserManagementAvailable: Definable<boolean> = useUserHasUserManagementPermission()

    // Fast Provisioning Available?
    const isFastProvisioningAvailable: Definable<boolean> = useMemo(
        (): Definable<boolean> => Or(isBroadworksProvisioningWizardAvailable, isQueueManagementAvailable, isUserManagementAvailable),
        [isBroadworksProvisioningWizardAvailable, isQueueManagementAvailable, isUserManagementAvailable]
    )

    // Should we show Admin entry?
    // ---
    // User has access to Admin?
    const isAdminAvailable: Definable<boolean> = useCheckUserHasPermissions(ADMIN_MENU_PERMISSIONS, true)

    // Should we show Status and Tools entry?
    // ---
    // User has access to Status and Tools?
    const isStatusAndToolsAvailable: Definable<boolean> = useCheckUserHasPermissions(STATUS_AND_TOOLS_PERMISSIONS, true)

    // The Classic/New UI toggle navigates the top window to the new UI, which fails with an
    // iframe-in-iframe error pop-up when CCS is embedded in the Teams app — so hide it there.
    const isTeamsApp: boolean = isNativeTeamsApp()

    const isNewTelsysListPageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_TELSYS_LIST_PAGE, false)
    const isNewCmeListPageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_CME_LIST_PAGE, false)
    const isNewAgentListPageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_AGENT_LIST_PAGE, false)
    const isNewPartitionListPageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_PARTITION_LIST_PAGE, false)
    const isNewCodeListPageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_CODE_LIST_PAGE, false)
    const isNewRecordingListPageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_RECORDING_LIST_PAGE, false)
    const isNewDirectoryListPageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_DIRECTORY_LIST_PAGE, false)
    const isNewUsersListPageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_USERS_LIST_PAGE, false)
    const isNewDeviceListPageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_DEVICE_LIST_PAGE, false)
    const isNewServerStatusPageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_SERVER_STATUS_PAGE, false)
    const isNewLicenseManagementPageEnabled: Definable<boolean> = useFeatureFlag(
        ENABLE_NEW_LICENSE_MANAGEMENT_PAGE,
        false
    )
    const isNewAuditTrailPageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_AUDIT_TRAIL_PAGE, false)
    const isNewLogManagementPageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_LOG_MANAGEMENT_PAGE, false)
    const isNewLoggingSettingsPageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_LOGGING_SETTINGS_PAGE, false)
    const isNewTelModuleStatusPageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_TEL_MODULE_STATUS_PAGE, false)
    const isNewDatabasePageEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_DATABASE_PAGE, false)

    // What options are shown in the menu?
    const appMenuEntries: Definable<AppMenuEntry[]> = useMemo((): Definable<AppMenuEntry[]> => { // NOSONAR
        if (
            isVarNotEmpty(isReportingAvailable) &&
            isVarNotEmpty(isFastProvisioningAvailable) &&
            isVarNotEmpty(isAdminAvailable) &&
            isVarNotEmpty(isStatusAndToolsAvailable)
        ) {
            return [
                {
                    enabled: isReportingAvailable,
                    title: language.get(AppMenuLanguage.AppMenu_Apps_Reporting),
                    icon: IconName.REPORTING,
                    menu: MainMenuApp.REPORTING,
                    pages: [RouteID.REPORTING_PORTAL, RouteID.INSIGHTS, RouteID.PRESENCE, RouteID.CXSCORE],
                    oldPages: [Page.REPORTING, Page.PRESENCE, Page.INSIGHTS_HUB]
                },
                {
                    enabled: isFastProvisioningAvailable,
                    title: language.get(AppMenuLanguage.AppMenu_Apps_FastProvisioning),
                    icon: IconName.FAST_PROVISIONING,
                    menu: MainMenuApp.FAST_PROVISIONING,
                    pages: [RouteID.LICENCE_MANAGEMENT]
                },
                {
                    enabled: isAdminAvailable,
                    title: language.get(AppMenuLanguage.AppMenu_Apps_Administration),
                    icon: IconName.ADMINISTRATION,
                    menu: MainMenuApp.ADMIN,
                    pages: [
                        RouteID.ADMIN,
                        ...(isNewTelsysListPageEnabled ? [RouteID.TELSYS] : []),
                        ...(isNewCmeListPageEnabled ? [RouteID.CME] : []),
                        ...(isNewAgentListPageEnabled ? [RouteID.AGENT] : []),
                        ...(isNewPartitionListPageEnabled ? [RouteID.PARTITION] : []),
                        ...(isNewUsersListPageEnabled ? [RouteID.USERS] : []),
                        ...(isNewDeviceListPageEnabled ? [RouteID.DEVICE] : []),
                        ...(isNewCodeListPageEnabled ? [RouteID.CODE] : []),
                        ...(isNewRecordingListPageEnabled ? [RouteID.RECORDING] : []),
                        RouteID.MESSAGES,
                        ...(isNewDatabasePageEnabled ? [RouteID.DATABASE] : []),
                        ...(isNewDirectoryListPageEnabled ? [RouteID.DIRECTORY] : []),
                        RouteID.CLIENT_CONFIGURATION,
                        RouteID.CRM_INTEGRATION
                    ],
                    oldPages: [
                        ...(isNewDeviceListPageEnabled ? [] : [Page.ADMIN_DEVICES]),
                        ...(isNewAgentListPageEnabled ? [] : [Page.ADMIN_AGENTS]),
                        ...(isNewPartitionListPageEnabled ? [] : [Page.ADMIN_PARTITIONS]),
                        Page.ADMIN_TELSYSS,
                        ...(isNewCmeListPageEnabled ? [] : [Page.ADMIN_CMENVIRONMENTS]),
                        ...(isNewUsersListPageEnabled ? [] : [Page.ADMIN_USERS]),
                        ...(isNewCodeListPageEnabled ? [] : [Page.ADMIN_CODES]),
                        ...(isNewDirectoryListPageEnabled ? [] : [Page.ADMIN_DIRECTORY]),
                        Page.ADMIN_CALLRECINTS,
                        ...(isNewDatabasePageEnabled ? [] : [Page.ADMIN_DATABASE]),
                        Page.ADMIN_DIRECTORY
                    ]
                },
                {
                    enabled: isStatusAndToolsAvailable,
                    title: language.get(AppMenuLanguage.AppMenu_Apps_StatusAndTools),
                    icon: IconName.STATUS_TOOLS,
                    menu: MainMenuApp.STATUS,
                    pages: [
                        ...(isNewServerStatusPageEnabled ? [RouteID.STATUS] : []),
                        ...(isNewLicenseManagementPageEnabled ? [RouteID.LICENCES] : []),
                        ...(isNewAuditTrailPageEnabled ? [RouteID.AUDIT_TRAIL] : []),
                        ...(isNewLogManagementPageEnabled ? [RouteID.LOG_MANAGEMENT] : []),
                        ...(isNewLoggingSettingsPageEnabled ? [RouteID.LOGGING_SETTINGS] : []),
                        ...(isNewTelModuleStatusPageEnabled ? [RouteID.TEL_MODULE_STATUS] : [])
                    ],
                    oldPages: [
                        ...(isNewLicenseManagementPageEnabled ? [] : [Page.LICENSEMGR]),
                        ...(isNewLogManagementPageEnabled ? [] : [Page.LOGFILEMGR]),
                        ...(isNewLoggingSettingsPageEnabled ? [] : [Page.ADMIN_LOGGING]),
                        Page.TELMODULE_CALLCTRL,
                        ...(isNewTelModuleStatusPageEnabled ? [] : [Page.TELMODULE_STATUS]),
                        ...(isNewServerStatusPageEnabled ? [] : [Page.APP_STATUS]),
                        ...(isNewAuditTrailPageEnabled ? [] : [Page.AUDITTRAIL])
                    ]
                }
            ]
        }

        return []
    }, [
        isReportingAvailable,
        isFastProvisioningAvailable,
        isAdminAvailable,
        isStatusAndToolsAvailable,
        isNewTelsysListPageEnabled,
        isNewCmeListPageEnabled,
        isNewAgentListPageEnabled,
        isNewPartitionListPageEnabled,
        isNewCodeListPageEnabled,
        isNewRecordingListPageEnabled,
        isNewDirectoryListPageEnabled,
        isNewUsersListPageEnabled,
        isNewDeviceListPageEnabled,
        isNewServerStatusPageEnabled,
        isNewLicenseManagementPageEnabled,
        isNewAuditTrailPageEnabled,
        isNewLogManagementPageEnabled,
        isNewLoggingSettingsPageEnabled,
        isNewTelModuleStatusPageEnabled,
        isNewDatabasePageEnabled,
        routeID,
        language.getLanguageID()
    ])

    const currentPage: Definable<MainMenuApp> = useMemo(
        (): Definable<MainMenuApp> =>
            (
                appMenuEntries?.find(
                    (entry: AppMenuEntry): boolean =>
                        !!(
                            (isStringNotEmpty(routeID) && entry.pages?.includes(routeID)) ||
                            (isStringNotEmpty(currentJSPPage) && entry.oldPages?.includes(currentJSPPage))
                        )
                ) ?? appMenuEntries?.[0]
            )?.menu,
        [appMenuEntries, routeID, currentJSPPage]
    )

    const appCount: number = useMemo(
        (): number => appMenuEntries?.filter(({ enabled }: AppMenuEntry): boolean => enabled)?.length ?? 0,
        [JSON.stringify(appMenuEntries)]
    )

    const showAppMenu: boolean = appCount > 1

    const appMenuState: RxJSCurrentState<MainMenuAppTransfer> = useRxJSState(appMenuUpdate$, {
        app: currentPage ?? MainMenuApp.REPORTING,
        showAppMenu
    })

    useEffect((): void => {
        appMenuUpdate$.announce({
            app: currentPage ?? MainMenuApp.REPORTING,
            showAppMenu
        })
    }, [appCount, currentPage, showAppMenu])

    useEffect((): void => {
        getNewUIConfig()
            .then((config: JSONResponse<NewUIConfigDTO>): void => {
                if (!isMounted.current) return

                setNewUIURL(getConfiguredNewUIURL(config))
            })
            .catch((): void => {
                if (!isMounted.current) return

                setNewUIURL(null)
            })
    }, [isMounted])

    useEffect((): void => {
        if (
            isVarNotEmpty(hasReportingAccess) &&
            isVarNotEmpty(isInsightsAvailable) &&
            isVarNotEmpty(isPresenceAvailable) &&
            isVarNotEmpty(isUserManagementAvailable) &&
            isVarNotEmpty(isQueueManagementAvailable) &&
            isVarNotEmpty(isBroadworksProvisioningWizardAvailable)
        ) {
            userPagePermissions$.announce({
                reporting: {
                    reports: hasReportingAccess ?? false,
                    insights: isInsightsAvailable ?? false,
                    presence: isPresenceAvailable ?? false
                },
                fastProvisioning: {
                    userManagement: isUserManagementAvailable ?? false,
                    queueManagement: isQueueManagementAvailable ?? false,
                    selfProvisioning: isBroadworksProvisioningWizardAvailable ?? false
                }
            })
        }
    }, [
        hasReportingAccess,
        isInsightsAvailable,
        isPresenceAvailable,
        isUserManagementAvailable,
        isQueueManagementAvailable,
        isBroadworksProvisioningWizardAvailable
    ])

    const switchToNewUi = (): void => {
        if (!newUIURL) return

        // The CCS SSO endpoint handles the Cognito custom:new_ui update and the token exchange
        // in one server-side call — the old frontend never contacts the facade directly.
        requestGetJSON<NewUISsoResponse>(buildNewUiSsoUrl())
            .then((pSsoResponse: JSONResponse<NewUISsoResponse>): void => {
                if (!pSsoResponse?.session_token) throw new Error('New UI SSO exchange returned no session_token')

                const pTargetURL: URL = new URL(newUIURL, window.location.origin)
                pTargetURL.searchParams.set(FUSION_TOKEN_QUERY_PARAM, pSsoResponse.session_token)
                window.top?.location.assign(pTargetURL.toString())
            })
            .catch((pError: Error): void => displayError(pError))
    }

    return (
        <>
            <div className='rh-left'>
                {showAppMenu ? (
                    <div className='rh-menu-container'>
                        <IconButton
                            ref={anchorElAppLauncher}
                            iconName={IconName.MENU_LAUNCHER}
                            type={IconButtonType.BUTTON}
                            onClick={(): void => setIsAppMenuOpen((prev: boolean): boolean => !prev)}
                        />

                        <TransitionPopper
                            className='app-menu-popper-container'
                            isOpen={isAppMenuOpen}
                            setIsOpen={setIsAppMenuOpen}
                            anchorEl={anchorElAppLauncher}
                            Transition={Grow}
                            style={{ width: 'clamp(300px, 90%, 450px)' }}
                        >
                            <div className='app-menu-popper'>
                                <AppMenu
                                    appMenuState={appMenuState}
                                    appMenuEntries={appMenuEntries}
                                    closeMenu={(): void => setIsAppMenuOpen(false)}
                                />
                            </div>
                        </TransitionPopper>
                    </div>
                ) : (
                    <></>
                )}
                {IsStringEmpty(branding?.BrandingConfig?.imagery?.squareLogo) &&
                isStringNotEmpty(branding?.BrandingConfig?.imagery?.mainLogo) ? (
                    <a
                        href={branding?.BrandingConfig?.links?.mainSiteLink?.url ?? EMPTY_STRING}
                        target={branding?.BrandingConfig?.links?.mainSiteLink?.target}
                        title={branding?.BrandingConfig?.links?.mainSiteLink?.title}
                        className='rh-logo-container'
                    >
                        <img
                            className='rh-logo'
                            src={configureFullURLForLocalDevelopment(branding.BrandingConfig.imagery.mainLogo)}
                            alt='Main Logo'
                        />
                    </a>
                ) : (
                    <></>
                )}
            </div>
            <div className='rh-right'>
                {isStringNotEmpty(userInfo?.FullName) ? (
                    <>
                        {isStringNotEmpty(newUIURL) && !isTeamsApp && userInfo?.ShowUiVersionToggle ? (
                            <div className='rh-ui-toggle-container'>
                                <UiVersionToggle
                                    classicLabel={language.get(AppMenuLanguage.AppMenu_ClassicUI)}
                                    newLabel={language.get(AppMenuLanguage.AppMenu_NewUI)}
                                    badgeLabel={language.get(AppMenuLanguage.AppMenu_NewUIBadge)}
                                    onSwitchToNewUI={switchToNewUi}
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                        <ButtonBase
                            id='user-menu-button'
                            ref={anchorElUserMenu}
                            onClick={(): void => setIsUserMenuOpen((prev: boolean): boolean => !prev)}
                        >
                            <div className='rh-settings-container'>
                                <UserBadge name={userInfo?.FullName} />
                            </div>
                        </ButtonBase>

                        <TransitionPopper
                            className='user-menu-popper-container'
                            isOpen={isUserMenuOpen}
                            setIsOpen={setIsUserMenuOpen}
                            anchorEl={anchorElUserMenu}
                            Transition={Grow}
                            style={{ width: 'clamp(200px, 100%, 300px)' }}
                            clickAwayGuard={(event: MouseEvent): boolean =>
                                !(event.target as HTMLElement).closest('#user-menu-button')
                            }
                        >
                            <div className='user-menu-popper' style={{ transformOrigin: 'top' }}>
                                <UserMenu
                                    closeMenu={(openNotifications: boolean): void => {
                                        if (openNotifications) {
                                            setNotificationsOpen(true)
                                        }
                                        setIsUserMenuOpen(false)
                                    }}
                                />
                            </div>
                        </TransitionPopper>
                        <NotificationIcon isOpenState={[notificationsOpen, setNotificationsOpen]} disableIcon={true} />
                    </>
                ) : (
                    <></>
                )}
            </div>
        </>
    )
}
